import { useState, useEffect } from 'react';
import { db, storage } from '../utils/firebase';
import './AdminPanel.css';
import imageCompression from 'browser-image-compression';

import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

export default function AdminPanel() {
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [year, setYear] = useState('');
  const [items, setItems] = useState([]);

  const [editingItem, setEditingItem] = useState(null);
  const [existingMedia, setExistingMedia] = useState([]);
  const [mediaToDelete, setMediaToDelete] = useState([]);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    const snapshot = await getDocs(collection(db, 'portfolio'));
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setItems(data);
  };

  const handleUpload = async () => {
    if (!files.length || !title || !description || !year) {
      alert('Please fill all fields');
      return;
    }

    const media = [];

    for (const file of files) {
      let fileToUpload = file;

      if (file.type.startsWith('image')) {
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1600,
          useWebWorker: true,
        };

        fileToUpload = await imageCompression(file, options);
      }

      const fileRef = ref(storage, `portfolio/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, fileToUpload);
      const url = await getDownloadURL(fileRef);

      media.push({
        url,
        type: file.type.startsWith('video') ? 'video' : 'image',
        path: fileRef.fullPath,
      });
    }

    await addDoc(collection(db, 'portfolio'), {
      title,
      description,
      year,
      media,
      createdAt: serverTimestamp(),
    });

    alert('Uploaded!');
    resetForm();
    fetchItems();
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setTitle(item.title);
    setDescription(item.description);
    setYear(item.year);
    setExistingMedia(item.media || []);
    setMediaToDelete([]);
  };

  const handleRemoveMedia = (mediaItem) => {
    setExistingMedia(prev => prev.filter(m => m !== mediaItem));
    setMediaToDelete(prev => [...prev, mediaItem]);
  };

  const handleUpdate = async () => {
    if (!editingItem) return;

    let updatedMedia = [...existingMedia];

    // Upload new files
    for (const file of files) {
      let fileToUpload = file;

      if (file.type.startsWith('image')) {
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1600,
          useWebWorker: true,
        };

        fileToUpload = await imageCompression(file, options);
      }

      const fileRef = ref(storage, `portfolio/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, fileToUpload);
      const url = await getDownloadURL(fileRef);

      updatedMedia.push({
        url,
        type: file.type.startsWith('video') ? 'video' : 'image',
        path: fileRef.fullPath,
      });
    }

    // Delete removed media from Firebase
    for (const media of mediaToDelete) {
      try {
        const fileRef = ref(storage, media.path);
        await deleteObject(fileRef);
      } catch (e) {
        console.warn('Delete skipped:', e.message);
      }
    }

    await updateDoc(doc(db, 'portfolio', editingItem.id), {
      title,
      description,
      year,
      media: updatedMedia,
    });

    alert('Updated!');
    resetForm();
    fetchItems();
  };

  const handleDelete = async (itemId, media) => {
    if (!window.confirm('Delete this item?')) return;

    for (const file of media) {
      const fileRef = ref(storage, file.path);
      await deleteObject(fileRef);
    }

    await deleteDoc(doc(db, 'portfolio', itemId));
    fetchItems();
  };

  const resetForm = () => {
    setFiles([]);
    setTitle('');
    setDescription('');
    setYear('');
    setEditingItem(null);
    setExistingMedia([]);
    setMediaToDelete([]);
  };

  return (
    <div className="admin-panel">
      <div className="admin-form">
        <h2>{editingItem ? 'Edit Work' : 'Upload New Work'}</h2>

        <input
          type="file"
          multiple
          onChange={e => setFiles([...e.target.files])}
        />

        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />

        <textarea
          placeholder="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <input
          type="text"
          placeholder="Year"
          value={year}
          onChange={e => setYear(e.target.value)}
        />

        {editingItem ? (
          <>
            <h4>Existing Media</h4>
            <div className="media-preview">
              {existingMedia.map((m, i) =>
                m.type === 'image' ? (
                  <div key={i}>
                    <img src={m.url} alt="" width="100" />
                    <button onClick={() => handleRemoveMedia(m)}>Remove</button>
                  </div>
                ) : (
                  <div key={i}>
                    <video src={m.url} width="100" />
                    <button onClick={() => handleRemoveMedia(m)}>Remove</button>
                  </div>
                )
              )}
            </div>

            <button onClick={handleUpdate}>Update</button>
            <button onClick={resetForm}>Cancel</button>
          </>
        ) : (
          <button onClick={handleUpload}>Upload</button>
        )}
      </div>

      <div className="admin-gallery">
        <h3>Uploaded Items</h3>

        {items.map(item => (
          <div key={item.id} className="admin-item">
            <h4>{item.title}</h4>
            <p>{item.description}</p>
            <p>{item.year}</p>

            <button onClick={() => handleEdit(item)}>Edit</button>
            <button onClick={() => handleDelete(item.id, item.media)}>
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}